import {
  SpaceBetween,
  Alert,
  CodeEditor,
  CodeEditorProps,
  Checkbox,
} from "@amzn/awsui-components-console";
import CodeView from "@amzn/awsui-code-view-components-console/code-view";
import jsonHighlight from "@amzn/awsui-code-view-components-console/highlight/json";
import { Registration } from "@amzn/fuse-accounting-central-datastore-client";
import React, { useEffect, useState } from "react";

// Import all necessary CSS for the themes
import "ace-builds/css/ace.css";
import "ace-builds/css/theme/cloud_editor.css";
import "ace-builds/css/theme/cloud_editor_dark.css";

import jsonWorkerPath from "ace-builds/src-min-noconflict/worker-json";

const CODE_EDITOR_I18N_STRINGS = {
  loadingState: "Loading code editor",
  errorState: "There was an error loading the code editor.",
  errorStateRecovery: "Retry",
  editorGroupAriaLabel: "Code editor",
  statusBarGroupAriaLabel: "Status bar",
  cursorPosition: (row: number, column: number) => `Ln ${row}, Col ${column}`,
  errorsTab: "Errors",
  warningsTab: "Warnings",
  preferencesButtonAriaLabel: "Preferences",
  paneCloseButtonAriaLabel: "Close",
  preferencesModalHeader: "Preferences",
  preferencesModalCancel: "Cancel",
  preferencesModalConfirm: "Confirm",
  preferencesModalWrapLines: "Wrap lines",
  preferencesModalTheme: "Theme",
  preferencesModalLightThemes: "Light themes",
  preferencesModalDarkThemes: "Dark themes",
};

export interface RegistrationEditorContentProps {
  isCreateMode: boolean;
  isEditMode: boolean;
  registration: Registration | null;
  loading: boolean;
  error: string | null;
  editedContent: string;
  isUpdating: boolean;
  linkToAllPlans: boolean;
  onEditorChange: (value: string) => void;
  onLinkToAllPlansChange: (checked: boolean) => void;
}

/**
 * Sub-component rendering the editor/viewer content area of the accounting registration modal.
 * Owns the ace editor instance and user preferences internally.
 */
export function RegistrationEditorContent({
  isCreateMode,
  isEditMode,
  registration,
  loading,
  error,
  editedContent,
  isUpdating,
  linkToAllPlans,
  onEditorChange,
  onLinkToAllPlansChange,
}: RegistrationEditorContentProps) {
  const [ace, setAce] = useState<any>(null);
  const [preferences, setPreferences] = useState<CodeEditorProps.Preferences>({
    wrapLines: false,
    theme: "cloud_editor",
  });

  // Configure ace library (lazy-load)
  useEffect(() => {
    const loadAce = async () => {
      const aceModule = await import("ace-builds");
      aceModule.config.set("useStrictCSP", true);

      // Point to where webpack serves the worker files
      aceModule.config.setModuleUrl(
        "ace/mode/json_worker",
        jsonWorkerPath as any,
      );

      // Import mode and theme modules
      await import("ace-builds/src-noconflict/mode-json");
      await import("ace-builds/src-noconflict/theme-cloud_editor");
      await import("ace-builds/src-noconflict/theme-cloud_editor_dark");

      setAce(aceModule);
    };
    loadAce();
  }, []);

  const registrationCodeEditor = (
    <CodeEditor
      ace={ace}
      language="json"
      value={editedContent}
      preferences={preferences}
      onPreferencesChange={(e) => setPreferences(e.detail)}
      onChange={(e) => onEditorChange(e.detail.value)}
      loading={isUpdating || !ace}
      themes={{
        dark: ["cloud_editor_dark"],
        light: ["cloud_editor"],
      }}
      i18nStrings={CODE_EDITOR_I18N_STRINGS}
    />
  );

  return (
    <>
      {/* Create mode: always show editor */}
      {isCreateMode && (
        <SpaceBetween size="m">
          <Alert type="info">
            Fill in the registration details below. Fields have been
            pre-populated from the ARD where possible. You must fill in{" "}
            <strong>carveSplitName</strong> and verify the{" "}
            <strong>transactionType</strong>.
          </Alert>
          <Checkbox
            checked={linkToAllPlans}
            onChange={({ detail }) => onLinkToAllPlansChange(detail.checked)}
          >
            Link to all plans in ARD
          </Checkbox>
          {registrationCodeEditor}
        </SpaceBetween>
      )}

      {/* View/Edit mode: show existing registration */}
      {!isCreateMode && registration && !loading && !error && (
        <>
          {isEditMode ? (
            <SpaceBetween size="m">
              <Alert type="info">
                Note: The <strong>accountingRegistrationId</strong> field cannot
                be modified.
              </Alert>
              {registrationCodeEditor}
            </SpaceBetween>
          ) : (
            <CodeView
              content={JSON.stringify(registration, null, 2)}
              highlight={jsonHighlight}
            />
          )}
        </>
      )}
    </>
  );
}
